import { useQuery, UseQueryResult } from '@tanstack/react-query';
import useSchedulingRequests from '.';
import staleTimeSettings from '../../../config/reactQueryConfig';
import { Paginated } from '../../../models/Paginated';
import {
  ActualShiftResponseDto,
  PlannedShiftResponseDto,
  SalesSummaryData,
  SalesSummaryDataBodySchema,
  SalesSummaryDataForWeekBodySchema,
  ScheduleResponse,
  ScheduleWeeksRangeResponse,
  WorkWeekConfig,
  WorkWeekConfigResponse,
  WorkWeeksForJurisdictionResponse,
  CopySchedulePreviewResponse,
  EmployeeShiftOverlap,
} from '../types';
import queryKeys from './queryKeys';
import useCurrentSite from '../../../utils/hooks/useCurrentSite';
import { IApiError } from '../../../services/types';

export const useSchedulesQuery = (
  calendarWeekStart: string,
  calendarWeekEnd: string,
  enabled: boolean
): UseQueryResult<ScheduleResponse[]> => {
  const { getSchedules } = useSchedulingRequests();

  return useQuery(
    queryKeys.schedules.listByCalendarWeek(calendarWeekStart, calendarWeekEnd),
    () => getSchedules(calendarWeekStart, calendarWeekEnd),
    {
      staleTime: staleTimeSettings.NONE,
      useErrorBoundary: true,
      keepPreviousData: true,
      enabled,
    }
  );
};

export const useScheduleBySelfQuery = (
  calendarWeekStart: string,
  calendarWeekEnd: string,
  enabled: boolean
): UseQueryResult<ScheduleResponse[]> => {
  const { getSchedulesBySelf } = useSchedulingRequests();

  return useQuery(
    queryKeys.schedules.selfListByCalendarWeek(calendarWeekStart, calendarWeekEnd),
    () => getSchedulesBySelf(calendarWeekStart, calendarWeekEnd),
    {
      staleTime: staleTimeSettings.LOW,
      useErrorBoundary: true,
      keepPreviousData: true,
      enabled,
    }
  );
};

export const usePlannedShiftsQuery = (
  page: number,
  limit: number,
  startDate: string,
  endDate: string,
  isAuthenticated: boolean
): UseQueryResult<Paginated<PlannedShiftResponseDto>> => {
  const { getPlannedShifts } = useSchedulingRequests();
  const { siteId } = useCurrentSite();
  return useQuery(
    queryKeys.schedules.plannedShiftsByRange(startDate, endDate, siteId),
    () => getPlannedShifts(page, limit, startDate, endDate),
    {
      staleTime: staleTimeSettings.LOW,
      useErrorBoundary: true,
      keepPreviousData: true,
      enabled: isAuthenticated,
    }
  );
};

export const useWorkWeekConfigQuery = (
  isAuthenticated: boolean
): UseQueryResult<WorkWeekConfig | WorkWeekConfigResponse> => {
  const { getWorkWeekConfig } = useSchedulingRequests();

  return useQuery(queryKeys.schedules.dayOfWeekStart, () => getWorkWeekConfig(), {
    staleTime: staleTimeSettings.HIGH,
    useErrorBoundary: true,
    keepPreviousData: true,
    enabled: isAuthenticated,
  });
};

export const useWorkWeekConfigsForJurisdiction = (
  jurisdictionPath: string,
  isAuthenticated: boolean
): UseQueryResult<WorkWeeksForJurisdictionResponse> => {
  const { getWorkWeekConfigsForJurisdiction } = useSchedulingRequests();

  return useQuery(
    queryKeys.schedules.workWeekConfigsByJurisdiction(jurisdictionPath),
    () => getWorkWeekConfigsForJurisdiction(jurisdictionPath),
    {
      staleTime: staleTimeSettings.HIGH,
      useErrorBoundary: false,
      keepPreviousData: true,
      enabled: isAuthenticated,
    }
  );
};

export const useActualShiftsQuery = (
  page: number,
  limit: number,
  startDate: string,
  endDate: string,
  isAuthenticated: boolean,
  employeeId?: string
): UseQueryResult<Paginated<ActualShiftResponseDto>> => {
  const { siteId } = useCurrentSite();
  const { getActualShifts } = useSchedulingRequests();

  return useQuery(
    queryKeys.schedules.actualShiftsByRange(startDate, endDate, siteId, employeeId),
    () => getActualShifts(page, limit, startDate, endDate, employeeId),
    {
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: true,
      keepPreviousData: true,
      enabled: isAuthenticated,
    }
  );
};

export const useGetSchedulePreviewQuery = (
  sourceStartDate: string,
  targetStartDate: string,
  enabled: boolean
): UseQueryResult<CopySchedulePreviewResponse, IApiError> => {
  const { getCopySchedulePreview } = useSchedulingRequests();

  return useQuery(
    queryKeys.schedules.preview(sourceStartDate, targetStartDate),
    () => getCopySchedulePreview(sourceStartDate, targetStartDate),
    {
      staleTime: staleTimeSettings.LOW,
      useErrorBoundary: false,
      keepPreviousData: false,
      enabled,
    }
  );
};

export const useGetScheduleWeeksRangeQuery = (
  siteId: string,
  enabled: boolean
): UseQueryResult<ScheduleWeeksRangeResponse> => {
  const { getScheduleWeeksRange } = useSchedulingRequests();

  return useQuery(queryKeys.schedules.weeksRange(siteId), () => getScheduleWeeksRange(), {
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
    keepPreviousData: true,
    enabled,
  });
};

export const useSalesSummaryQuery = (
  scheduleId: string | undefined,
  enabled: boolean,
  payload: SalesSummaryDataBodySchema
): UseQueryResult<SalesSummaryData[]> => {
  const { getSalesSummary } = useSchedulingRequests();

  return useQuery(
    queryKeys.schedules.summarySales(scheduleId || ''),
    () =>
      scheduleId
        ? getSalesSummary(scheduleId, payload)
        : [{} as SalesSummaryData, {} as SalesSummaryData, {} as SalesSummaryData],
    {
      staleTime: staleTimeSettings.LOW,
      useErrorBoundary: false,
      keepPreviousData: false,
      enabled,
    }
  );
};

export const useSalesSummaryForWeekQuery = (
  enabled: boolean,
  payload: SalesSummaryDataForWeekBodySchema
): UseQueryResult<SalesSummaryData[]> => {
  const { getSalesSummaryForWeek } = useSchedulingRequests();

  return useQuery(
    queryKeys.schedules.summarySalesForWeek(payload.startDate, payload.endDate),
    () => getSalesSummaryForWeek(payload),
    {
      staleTime: staleTimeSettings.LOW,
      useErrorBoundary: false,
      keepPreviousData: false,
      enabled,
    }
  );
};

export const useOverlappedShiftsEmployeeIdsQuery = (
  calendarWeekStart: string,
  calendarWeekEnd: string,
  enabled: boolean
): UseQueryResult<EmployeeShiftOverlap[]> => {
  const { getOverlapedShiftsEmployeesIds } = useSchedulingRequests();

  return useQuery(
    queryKeys.schedules.overlappedEmployeeIds(calendarWeekStart, calendarWeekEnd),
    () => getOverlapedShiftsEmployeesIds(calendarWeekStart, calendarWeekEnd),
    {
      staleTime: staleTimeSettings.NONE,
      useErrorBoundary: false,
      keepPreviousData: false,
      enabled,
    }
  );
};
